import React, { useEffect, useState } from 'react';

import { Row, Col, Button, Form, Collapse } from 'antd';
import { MinusCircleOutlined } from '@ant-design/icons';
import styles from './FormView.module.css';
import CrossHair from '../../assets/img/crosshair.svg';

import {
  Input,
  PasswordInput,
  Textarea,
  Select,
  Radio,
  Checkbox,
  Switch,
  Tags,
  MediaUploader,
  PlacesAutocompleteInput,
  DatePicker,
  EmailsTags,
  SelectGroupMembers,
} from './Fields';

import {
  FormViewProps,
  FormField,
  FormFieldStructure,
  FormFields,
} from './types';
import { UploadedMediaType } from '../MediaUploader/types';
import dayjs from 'dayjs';
import { SelectDirectoryMembers } from './Fields/SelectDirectoryMembers';
import { useUser } from 'src/contexts/User/User';
import { CUSTOMER_TYPES, DEMAND_RESOLVED } from 'src/constants';
import { useForceRendering } from 'src/hooks/useForceRendering';
import { comparerObjects } from 'src/features/utils';
import { ImageCropUploader } from '../ImageCropUploader/ImageCropUploader';
import { LimitParticipant } from '../LimitParticipant/LimitParticipant';
import { useFormValuesObserver } from './FormValuesContext';

const { Panel } = Collapse;

export const FormView: React.FC<FormViewProps> = ({
  isTarget,
  formStructure,
  initialValues = {},
  fields,
  footer,
  directory,
  disabledSelect,
  disabledByRight,
  hasError,
  errorMessage,
  demandPublishStatus,
  isEdit,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const { user } = useUser();
  const [startDate, setStartDate] = useState();
  const [newInitialValues, setNewInitialValue] = useState();

  const forceRendering = useForceRendering();

  /* Live values for an optional preview: antd does not fire onValuesChange on
     programmatic updates, so every setFieldsValue goes through this wrapper */
  const observeValues = useFormValuesObserver();
  const notifyValues = () => observeValues?.(form.getFieldsValue(true));
  const setFieldsValue = (values: Record<string, any>) => {
    form.setFieldsValue(values);
    notifyValues();
  };

  if (initialValues.startDate && !!!startDate)
    setStartDate(initialValues.startDate);

  if (hasError) {
    form.resetFields();
  }

  const submitHandler = () => {
    onSubmit(form.getFieldsValue());
  };

  const disabledStartDate = (current: any) =>
    current && current < dayjs().subtract(1, 'day');

  const disabledEndDate = (current: any) => current < dayjs(startDate);

  const disabledBirthDate = (current: any) => current > dayjs();

  const getDisableDate = (name: string) => {
    switch (name) {
      case 'birth_date':
        return disabledBirthDate;
      case 'startDate':
        return disabledStartDate;
      case 'endDate':
        return disabledEndDate;
      default:
        return () => false;
    }
  };

  const buildField = (
    {
      id,
      name,
      picker,
      type,
      label,
      placeholder,
      secondLabel,
      options,
      rules,
      icon,
      isDisabled,
      limit,
      pictureListType,
      imageOnly,
      withVideo,
      withNetworks,
      networkId,
      canRemove,
      isAssociation,
      mode,
      dropdownStyle,
      groupId,
    }: FormField,
    index?: number
  ) => {
    var field;

    if ((name === DEMAND_RESOLVED || name === 'meetAt') && !isEdit) return;
    if (
      (isEdit || user?.customer.type !== CUSTOMER_TYPES.CCAS) &&
      name === 'isNotifiable'
    )
      return;

    if (
      user?.customer.type !== CUSTOMER_TYPES.NATIONAL &&
      (name === 'notifyDistance' || name === 'notifyPerimeter')
    )
      return;

    if (
      user?.customer.type !== CUSTOMER_TYPES.CCAS &&
      name === 'categoryDescription'
    )
      return;

    switch (type) {
      case 'text':
      case 'email':
      case 'tel':
        /* for field with type list, to specify a numbering for each item in the list */
        const indexedPlaceholder =
          index !== undefined ? `${placeholder} ${index + 3}` : placeholder;
        if (user?.customer.type === CUSTOMER_TYPES.NATIONAL) {
          field = Input({
            id,
            type,
            placeholder:
              name === 'customer' ? initialValues[name] : indexedPlaceholder,
            icon,
            isDisabled: isDisabled || disabledByRight,
          });
        } else {
          field =
            name !== 'customer_name_entity'
              ? Input({
                  id,
                  type,
                  placeholder:
                    name === 'customer'
                      ? initialValues[name]
                      : indexedPlaceholder,
                  icon,
                  isDisabled: isDisabled || disabledByRight,
                })
              : null;
        }

        break;
      case 'password':
        field = PasswordInput({ id, placeholder });
        break;
      case 'limitParticipant':
        field = LimitParticipant({
          id,
          placeholder,
          defaultValue: initialValues[name] || undefined,
          onChange: value => {
            setFieldsValue({ [name]: value });
          },
          mode,
          dropdownStyle,
        });
        break;
      case 'select':
        field = Select({
          id,
          name,
          placeholder,
          defaultValue: form.getFieldValue(name) || undefined,
          options,
          isDisabled: isDisabled || disabledByRight,
          mode,
          dropdownStyle,
        });
        break;
      case 'selectDirectoryMembers':
        field = SelectDirectoryMembers({
          id,
          name,
          placeholder,
          defaultValue:
            form.getFieldValue(name) || initialValues[name] || undefined,
          onChange: value => {
            setFieldsValue({ [name]: value });
          },
          directory,
          disabledSelect,
        });
        break;
      case 'selectGroupMembers':
        field = SelectGroupMembers({
          id,
          placeholder,
          groupId,
          defaultValue: form.getFieldValue(name) || initialValues[name] || [],
          onChange: value => {
            setFieldsValue({ [name]: value });
          },
        });
        break;
      case 'tags':
        field = Tags({
          id,
          name,
          placeholder,
          defaultValue: form.getFieldValue(name) || [],
          options,
          withNetworks,
          networkId,
          onChange: tags => setFieldsValue({ [name]: tags }),
        });
        break;
      case 'emailsTags':
        field = EmailsTags({
          id,
          placeholder,
          onChange: emails => setFieldsValue({ [name]: emails }),
        });
        break;
      case 'radio':
        field = Radio({
          id,
          placeholder,
          options,
          isDisabled: isDisabled || disabledByRight,
          defaultValue:
            Boolean(form.getFieldValue(name)) || initialValues[name],
        });
        break;
      case 'checkbox':
        field = Checkbox({
          id,
          name,
          placeholder,
          options,
          defaultChecked: form.getFieldValue(name),
          onChange: options => {
            setFieldsValue({ [name]: options });
          },
          isAssociation,
          isDisabled: isDisabled || disabledByRight,
        });
        break;
      case 'textArea':
        field = Textarea({
          id,
          placeholder,
          isDisabled: isDisabled || disabledByRight,
        });
        break;
      case 'switch':
        field = !demandPublishStatus
          ? Switch({
              id,
              placeholder,
              secondLabel,
              defaultChecked: initialValues[name] || false,
              name,
              onHandleChange: checked =>
                setFieldsValue({ [name]: checked }),
              isDisabled: isDisabled || disabledByRight,
            })
          : null;
        break;
      case 'mediaUploader':
        field = MediaUploader({
          id,
          name,
          defaultValue: initialValues[name] || [],
          placeholder,
          imageOnly,
          withVideo,
          limit,
          pictureListType: pictureListType || 'picture-card',
          onUpload: (mediaList: Array<UploadedMediaType>) => {
            setFieldsValue({ [name]: mediaList });
          },
          canRemove,
          isDisabled: isDisabled || disabledByRight,
        });
        break;
      case 'imageUploader':
        field = ImageCropUploader({
          defaultValue: initialValues[name] || [],
          id,
          pictureListType: pictureListType || 'picture-card',
          placeholder,
          onUpload: (mediaList: Array<UploadedMediaType>) => {
            setFieldsValue({ [name]: mediaList });
          },
        });
        break;
      case 'place':
        field = PlacesAutocompleteInput({
          id,
          placeholder,
          defaultValue: form.getFieldValue(name) || initialValues[name],
          onChange: place => setFieldsValue({ [name]: place?.place_id }),
          isDisabled: isDisabled || disabledByRight,
        });
        break;
      case 'date':
        field = DatePicker({
          id,
          placeholder,
          name,
          picker,
          defaultValue: form.getFieldValue(name) || initialValues[name],
          onChange: value => {
            setFieldsValue({ [name]: value });
            name === 'startDate' && setStartDate(value);
          },
          disabledDate: getDisableDate(name),
          isDisabled: isDisabled || disabledByRight,
        });
        break;

      default:
        break;
    }

    return (
      <Form.Item
        className={`${
          user?.customer.type !== CUSTOMER_TYPES.NATIONAL &&
          name === 'customer_name_entity'
            ? styles.hidden
            : styles.show
        }
          ${name === 'isPublic' && styles.isPublic}
          `}
        label={label}
        name={index !== undefined ? index : name}
        key={index !== undefined ? index : name}
        rules={rules}
        validateTrigger={type === 'email' ? 'onInvalid' : 'onChange'}
      >
        {field}
      </Form.Item>
    );
  };

  const buildCollapseFieldList = (listField: FormFieldStructure) => {
    if (!isTarget && listField.name === 'target') return false;

    return (
      listField.items && (
        <>
          <Collapse
            className={
              user?.customer.type === CUSTOMER_TYPES.HABITAT
                ? styles.containerCollapse
                : ''
            }
            defaultActiveKey={
              user?.customer.type !== CUSTOMER_TYPES.HABITAT ? ['2'] : ['1']
            }
          >
            <Panel
              id="target"
              showArrow={false}
              header={
                <div className={styles.panelTitle} id="target-header">
                  <img src={CrossHair} alt="CrossHair" /> {listField.header}{' '}
                  {user?.customer.type !== CUSTOMER_TYPES.HABITAT &&
                    '(Optionnel)'}{' '}
                </div>
              }
              key="1"
            >
              <Row>
                {listField.items.map(field => (
                  <Col span={24}>{buildField(fields[field.name])}</Col>
                ))}
              </Row>
            </Panel>
          </Collapse>
          {user?.customer.type !== CUSTOMER_TYPES.HABITAT &&
            user?.customer.type !== CUSTOMER_TYPES.DEPARTMENT && (
              <p className={styles.informations}>
                Si vous ne sélectionnez aucun quartier, alors la publication
                sera visible dans toute la ville.
              </p>
            )}
          {user?.customer.type === CUSTOMER_TYPES.DEPARTMENT && (
            <p className={styles.informations}>
              Si vous ne sélectionnez aucune ville, alors la publication sera
              visible dans tout le département.
            </p>
          )}
        </>
      )
    );
  };

  const buildFieldsGroup = (field: FormFieldStructure) => {
    if (
      user?.customer.type === CUSTOMER_TYPES.CCAS &&
      (field.name === 'demandInfos-1' || field.name === 'demandInfos-2')
    )
      return;

    return (
      field.items && (
        <>
          <Row key={`${field.name}`} gutter={8}>
            {field.items.map((item: FormFieldStructure) => (
              <Col
                key={`${field.name}-${item.name}`}
                span={field.items ? 24 / field.items.length : 24}
              >
                {buildField(fields[item.name])}
              </Col>
            ))}
          </Row>
          {field.items.map((item: FormFieldStructure) => {
            if (item.name === 'email' && errorMessage !== '') {
              return (
                <Row className={`${styles.fadein} ${styles.error}`}>
                  {errorMessage}
                </Row>
              );
            }
            return false;
          })}
        </>
      )
    );
  };

  const buildFieldList = (
    mainFields: FormFields,
    listField: FormFieldStructure
  ) => (
    <Form.List name={listField.name}>
      {(fields, { add, remove }) => (
        <>
          {fields.map((field, index) => (
            <Form.Item
              required={false}
              key={field.key}
              style={{ marginBottom: '0' }}
            >
              {listField.itemPattern &&
                buildField(mainFields[listField.itemPattern.name], index)}
              {fields.length >= 1 ? (
                <MinusCircleOutlined
                  style={{
                    color: 'red',
                    fontSize: '24px',
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                  }}
                  onClick={() => {
                    remove(field.name);
                  }}
                />
              ) : null}
            </Form.Item>
          ))}
          <Form.Item>
            <Button
              id="add_an_anwser"
              type="link"
              style={{ padding: '0' }}
              onClick={() => {
                add();
              }}
            >
              Ajouter une réponse
            </Button>
          </Form.Item>
        </>
      )}
    </Form.List>
  );

  useEffect(() => {
    if (comparerObjects(initialValues, newInitialValues)) return;

    setNewInitialValue(initialValues);
    // eslint-disable-next-line
  }, [initialValues]);

  useEffect(() => {
    form.resetFields();
    forceRendering();
    notifyValues();
    // eslint-disable-next-line
  }, [newInitialValues]);

  return (
    <Form
      form={form}
      onFinish={submitHandler}
      initialValues={newInitialValues}
      onValuesChange={notifyValues}
    >
      {formStructure.map(field => {
        if (field.type === 'group') {
          return buildFieldsGroup(field);
        }
        if (field.type === 'list') {
          return buildFieldList(fields, field);
        }
        if (field.type === 'collapseList') {
          return buildCollapseFieldList(field);
        }
        return buildField(fields[field.name]);
      })}
      <Form.Item>{footer}</Form.Item>
    </Form>
  );
};
